import { useEffect, useState } from 'react'
import { type Resolver, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Star, X, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import DateTimeField from '@/components/feature/DateTimeField'
import { useCreateTask, useUpdateTask } from '@/hooks/useTasks'
import { FREQUENCY_LABELS, type Task, type TaskFrequency } from '@/types'
import { RATING_COLORS } from '@/lib/rating-colors'

const schema = z.object({
  title: z.string().min(1, 'タイトルは必須です').max(255),
  start_date: z.string().optional(),
  start_time: z.string().optional(),
  due_date: z.string().optional(),
  due_time: z.string().optional(),
  repeat_end_date: z.string().optional(),
  intensity_value: z.coerce.number().positive().optional().or(z.literal('')),
  intensity_unit: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

type Props = {
  open: boolean
  onClose: () => void
  task?: Task
}

const FREQUENCY_OPTIONS: { value: TaskFrequency; label: string }[] = [
  { value: 'daily', label: FREQUENCY_LABELS.daily },
  { value: 'weekly', label: FREQUENCY_LABELS.weekly },
  { value: 'monthly', label: FREQUENCY_LABELS.monthly },
  { value: 'irregular', label: FREQUENCY_LABELS.irregular },
]

const WEEK_DAY_LABELS = ['月', '火', '水', '木', '金', '土', '日']

export default function TaskCreateSheet({ open, onClose, task }: Props) {
  const isEdit = !!task
  const createTask = useCreateTask()
  const updateTask = useUpdateTask()
  const isPending = isEdit ? updateTask.isPending : createTask.isPending

  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [frequency, setFrequency] = useState<TaskFrequency>('none')
  const [repeatOn, setRepeatOn] = useState<number[]>([])
  const [rating, setRating] = useState(1)
  const [useRepeat, setUseRepeat] = useState(false)
  const [activeTab, setActiveTab] = useState<'repeat' | 'due' | null>(null)
  const [confirmOverride, setConfirmOverride] = useState<'repeat' | 'due' | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const { register, handleSubmit, reset, control, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
  })

  // React Compiler 互換のため watch() ではなく useWatch でトップレベル購読する
  const dueDate = useWatch({ control, name: 'due_date' })
  const intensityValue = useWatch({ control, name: 'intensity_value' })
  const intensityUnit = useWatch({ control, name: 'intensity_unit' })

  useEffect(() => {
    if (!open) return
    // open / task 変更時にフォームと UI 状態をまとめて初期化する正当な同期処理のため、
    // 同期 setState を許容する（cascading render は open 時の一度きりで実害なし）
    /* eslint-disable react-hooks/set-state-in-effect */
    if (task) {
      reset({
        title: task.title,
        start_date: task.start_date ?? '',
        start_time: task.scheduled_time?.slice(0, 5) ?? '',
        due_date: task.due_date ?? '',
        due_time: task.due_time?.slice(0, 5) ?? '',
        repeat_end_date: task.repeat_end_date ?? '',
        intensity_value: task.intensity_value ?? '',
        intensity_unit: task.intensity_unit ?? '',
      })
      setTags(task.tags)
      setUseRepeat(task.frequency !== 'none')
      setActiveTab(null)
      setFrequency(task.frequency)
      setRepeatOn(task.repeat_on)
      setRating(task.rating)
      setShowAdvanced(!!(task.tags.length || task.intensity_value))
    } else {
      reset({
        title: '', start_date: '', start_time: '',
        due_date: '', due_time: '', repeat_end_date: '',
        intensity_value: '', intensity_unit: '',
      })
      setTags([])
      setTagInput('')
      setUseRepeat(false)
      setActiveTab(null)
      setFrequency('none')
      setRepeatOn([])
      setRating(1)
      setShowAdvanced(false)
    }
    /* eslint-enable react-hooks/set-state-in-effect */
    // open / task.id が変わった時だけ初期化する意図のため task・reset は依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id])

  const handleClose = () => {
    setTagInput('')
    onClose()
  }

  const handleTabClick = (tab: 'repeat' | 'due') => {
    if (activeTab === tab) { setActiveTab(null); return }
    if (tab === 'repeat' && !!dueDate) { setConfirmOverride('repeat'); return }
    if (tab === 'due' && frequency !== 'none') { setConfirmOverride('due'); return }
    setActiveTab(tab)
    if (tab === 'repeat' && !useRepeat) setUseRepeat(true)
  }

  const applyRepeat = (enabled: boolean) => {
    setUseRepeat(enabled)
    if (!enabled) { setFrequency('none'); setRepeatOn([]) }
    else { setValue('due_date', ''); setValue('due_time', ''); setFrequency('none'); setActiveTab('repeat') }
  }

  const applyDue = () => {
    setFrequency('none'); setRepeatOn([]); setActiveTab('due')
  }

  const handleFrequencyChange = (f: TaskFrequency) => {
    setFrequency(f)
    setRepeatOn([])
  }

  const toggleRepeatOn = (val: number) => {
    setRepeatOn((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val])
  }

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t)) setTags([...tags, t])
    setTagInput('')
  }

  const buildPayload = (values: FormValues): Partial<Task> => ({
    title: values.title,
    tags,
    frequency,
    start_date: values.start_date || null,
    scheduled_time: values.start_time || null,
    due_date: values.due_date || null,
    due_time: values.due_date && values.due_time ? values.due_time : null,
    repeat_on: ['weekly', 'monthly'].includes(frequency) ? repeatOn : [],
    repeat_end_date: useRepeat ? (values.repeat_end_date || null) : null,
    rating,
    intensity_value: values.intensity_value ? Number(values.intensity_value) : null,
    intensity_unit: values.intensity_unit ?? '',
  })

  const onSubmit = (values: FormValues) => {
    if (isEdit) {
      updateTask.mutate(
        { id: task.id, data: buildPayload(values) },
        {
          onSuccess: () => { toast.success('タスクを更新しました'); handleClose() },
          onError: () => toast.error('更新に失敗しました'),
        },
      )
    } else {
      createTask.mutate(
        buildPayload(values),
        {
          onSuccess: () => { toast.success('タスクを作成しました'); handleClose() },
          onError: () => toast.error('作成に失敗しました'),
        },
      )
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <SheetContent side="bottom" className="rounded-t-2xl flex flex-col max-h-[90dvh]">
        <SheetHeader className="shrink-0">
          <SheetTitle>{isEdit ? 'タスクを編集' : 'タスクを作成'}</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 overflow-y-auto flex-1 pb-4">
          {/* タイトル */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">タイトル *</label>
            <Input {...register('title')} placeholder="タスク名を入力" autoFocus />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>

          {/* 難易度 */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">難易度</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} type="button" onClick={() => setRating(star)}
                  className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Star size={28} className={star <= rating ? RATING_COLORS.self : RATING_COLORS.empty} />
                </button>
              ))}
            </div>
          </div>

          {/* 開始日 */}
          <DateTimeField
            label="開始日"
            dateField={register('start_date')}
            timeField={register('start_time')}
          />

          {/* 繰り返し / 期限 タブ */}
          <div className="border-b border-border">
            <div className="flex">
              {([
                { key: 'repeat', label: '繰り返し', configured: useRepeat && frequency !== 'none' },
                { key: 'due', label: '期限', configured: !!dueDate },
              ] as const).map(({ key, label, configured }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabClick(key)}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors min-h-[44px] border-b-2 -mb-px ${
                    activeTab === key
                      ? 'border-primary text-primary'
                      : configured
                        ? 'border-transparent text-primary/70'
                        : 'border-transparent text-muted-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 繰り返し 展開 */}
          {activeTab === 'repeat' && (
            <div className="space-y-2">
              <div className="flex gap-1.5">
                {FREQUENCY_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button" onClick={() => handleFrequencyChange(opt.value)}
                    className={`flex-1 py-2 text-sm rounded-md border transition-colors min-h-[44px] ${
                      frequency === opt.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {frequency === 'weekly' && (
                <div className="flex gap-1.5">
                  {WEEK_DAY_LABELS.map((label, i) => (
                    <button key={i} type="button" onClick={() => toggleRepeatOn(i)}
                      className={`flex-1 py-2 text-sm rounded-md border transition-colors min-h-[44px] ${
                        repeatOn.includes(i)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {frequency === 'monthly' && (
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <button key={day} type="button" onClick={() => toggleRepeatOn(day)}
                      className={`shrink-0 w-10 h-10 text-sm rounded-md border transition-colors flex items-center justify-center ${
                        repeatOn.includes(day)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              )}

              <DateTimeField
                label="繰り返し期限（任意）"
                dateField={register('repeat_end_date')}
                labelClassName="text-sm text-muted-foreground"
              />
            </div>
          )}

          {/* 期限 展開 */}
          {activeTab === 'due' && (
            <DateTimeField
              label="期限日"
              dateField={register('due_date')}
              timeField={register('due_time')}
            />
          )}

          {/* 詳細設定 */}
          <div>
            <button type="button" onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground min-h-[44px]"
            >
              詳細設定
              {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {!showAdvanced && (tags.length > 0 || intensityValue) && (
              <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                {tags.length > 0 && <span>タグ: {tags.join(', ')}</span>}
                {intensityValue && (
                  <span>強度: {intensityValue} {intensityUnit || ''}</span>
                )}
              </div>
            )}

            {showAdvanced && (
              <div className="space-y-4 mt-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">タグ</label>
                  <div className="flex gap-2">
                    <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                      placeholder="タグを入力して Enter"
                    />
                    <Button type="button" variant="outline" onClick={addTag} size="sm">追加</Button>
                  </div>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {tags.map((tag) => (
                        <span key={tag} className="flex items-center gap-1 text-xs bg-secondary text-secondary-foreground rounded-full px-2.5 py-1">
                          {tag}
                          <button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <div className="flex-1 space-y-1.5">
                    <label className="text-sm font-medium">強度（任意）</label>
                    <Input {...register('intensity_value')} type="number" placeholder="30" />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <label className="text-sm font-medium">単位</label>
                    <Input {...register('intensity_unit')} placeholder="分" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? (isEdit ? '更新中...' : '作成中...') : (isEdit ? '更新する' : '作成する')}
          </Button>
        </form>

        <ConfirmDialog
          open={confirmOverride !== null}
          onOpenChange={(v) => { if (!v) setConfirmOverride(null) }}
          title={confirmOverride === 'repeat' ? '期限がオフになりますがよろしいですか？' : '繰り返しがオフになりますがよろしいですか？'}
          confirmLabel="OK"
          onConfirm={() => {
            if (confirmOverride === 'repeat') applyRepeat(true)
            else applyDue()
            setConfirmOverride(null)
          }}
        />
      </SheetContent>
    </Sheet>
  )
}
